import { browserAgent } from "@/shared/telemetry/newrelic";

import type { TrackEventParams, Tracker, TrackingEvent } from './types';

declare global {
  interface Window {
    tracker?: Tracker;
  }
}

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function generateTimestamp(): string {
  return new Date().toISOString();
}

interface TrackerError extends Error {
  response?: { status: number; statusText: string };
  endpoint?: string;
  eventType?: string;
}

export class TrackerImpl implements Tracker {
  private authToken: string | null = null;
  private eventBuffer: TrackEventParams[] = [];
  public isInitialized = false;
  private readonly ENDPOINT = '/api/v1/events';
  private readonly APPLICATION = 'web-frontend';

  initialize(token: string): void {
    if (this.isInitialized) {
      console.warn(
        '[Tracker] Already initialized. Skipping re-initialization.',
      );
      return;
    }

    this.authToken = token;
    this.isInitialized = true;
    this.processBufferedEvents();
  }

  track(params: TrackEventParams): void {
    if (!this.isInitialized) {
      this.eventBuffer.push(params);
      return;
    }

    this.sendEvent(params);
  }

  private processBufferedEvents(): void {
    if (this.eventBuffer.length === 0) return;

    console.warn(
      `[Tracker] Processing ${this.eventBuffer.length} buffered events`,
    );

    for (const eventParams of this.eventBuffer) {
      this.sendEvent(eventParams);
    }
    this.eventBuffer = [];
  }

  private buildEvent(params: TrackEventParams): TrackingEvent {
    return {
      eventId: generateUUID(),
      requestId: generateUUID(),
      eventType: params.eventType,
      timestamp: generateTimestamp(),
      merchantId: params.merchantId,
      checkoutId: params.checkoutId,
      orderId: params.orderId,
      customerId: params.customerId,
      countryCode: params.countryCode,
      currencyCode: params.currencyCode,
      application: this.APPLICATION,
      eventData: params.eventData,
      spans: params.spans ?? [],
    };
  }

  private async sendEvent(params: TrackEventParams): Promise<void> {
    if (!this.authToken) {
      console.error('[Tracker] Cannot send event: no auth token');
      return;
    }

    const event = this.buildEvent(params);
    const payloadString = JSON.stringify([event]);

    try {
      const response = await fetch(this.ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.authToken}`,
        },
        body: payloadString,
        keepalive: true,
      });

      if (!response.ok) {
        this.throwResponseError(response, params);
      }
    } catch (error) {
      this.handleSendError(error, params);
    }
  }

  private throwResponseError(
    response: Response,
    params: TrackEventParams,
  ): never {
    const { status, statusText } = response;
    const suffix =
      status === 401 ? ' - Unauthorized (invalid or expired auth token)' : '';
    const error: TrackerError = new Error(
      `Failed to send event: ${status} ${statusText}${suffix}`,
    );
    error.response = { status, statusText };
    error.endpoint = this.ENDPOINT;
    error.eventType = params.eventType;
    throw error;
  }

  private handleSendError(error: unknown, params: TrackEventParams): void {
    const errorMessage =
      error instanceof Error ? error.message : String(error);
    const hasResponse =
      error != null && typeof error === 'object' && 'response' in error;
    const responseStatus = hasResponse
      ? (error as TrackerError).response?.status
      : undefined;
    const is401 = errorMessage.includes('401') || responseStatus === 401;
    const context = {
      endpoint: this.ENDPOINT,
      eventType: params.eventType,
      checkoutId: params.checkoutId,
      orderId: params.orderId,
    };

    if (is401) {
      console.warn(
        `[Tracker] 401 Unauthorized: ${params.eventType}`,
        { ...context, error: errorMessage },
      );
    } else {
      console.error(
        `[Tracker] Failed to send event: ${params.eventType}`,
        error,
        context,
      );
    }

    this.reportToTelemetry(params, errorMessage, responseStatus);
  }

  private reportToTelemetry(
    params: TrackEventParams,
    errorMessage: string,
    status: number | undefined,
  ): void {
    try {
      browserAgent.log('TRACKER_SEND_EVENT_FAILED', {
        customAttributes: {
          eventType: params.eventType,
          endpoint: this.ENDPOINT,
          checkoutId: params.checkoutId,
          orderId: params.orderId,
          error: errorMessage,
          status,
        },
      });
    } catch (telemetryError) {
      console.error('[Tracker] Failed to report to telemetry:', telemetryError);
    }
  }
}

export const trackerInstance = new TrackerImpl();
window.tracker = trackerInstance;
